'use client'

import { useAuth, useClerk } from '@clerk/nextjs'
import { useRouter } from 'next/navigation'
import { type MouseEvent, useCallback } from 'react'
import { isPlainClick } from './plain-click'

/** Where a signed-in visitor's "Start free" goes instead of the modal. */
export const SIGNED_IN_HOME_PATH = '/home'
/**
 * Where a new account lands, as on the `/sign-up` page: the setup loader
 * routes it to onboarding (which reads the website or prompt the home page
 * saved), org creation, or the invited member's path. The env fallback is
 * `/`, which would drop a new visitor back on the home page.
 */
const SIGN_UP_REDIRECT_PATH = '/setup'

/**
 * The public site's one way in: Clerk's sign-in modal, in place, in its
 * sign-in-or-up flow (`withSignUp`), so a returning member signs in and a
 * new visitor creates an account from the same modal, with no trip to the
 * `/sign-up` page. A sign-in lands in the app, a sign-up in onboarding
 * (`/setup`); a visitor already signed in goes to the app instead.
 */
export function useOpenAuthModal(): () => void {
  const { isSignedIn } = useAuth()
  const { openSignIn } = useClerk()
  const router = useRouter()
  return useCallback(() => {
    if (isSignedIn) {
      router.push(SIGNED_IN_HOME_PATH)
      return
    }
    openSignIn({
      withSignUp: true,
      fallbackRedirectUrl: SIGNED_IN_HOME_PATH,
      signUpFallbackRedirectUrl: SIGN_UP_REDIRECT_PATH,
    })
  }, [isSignedIn, openSignIn, router])
}

/**
 * For a real link to `/sign-up` (it works before hydration and without
 * JavaScript): a plain click opens the auth modal in place, while a
 * Ctrl/Cmd/middle click keeps the browser's new tab.
 */
export function useAuthModalLinkClick(): (
  event: MouseEvent<HTMLAnchorElement>
) => void {
  const open = useOpenAuthModal()
  return useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      if (!isPlainClick(event)) {
        return
      }
      event.preventDefault()
      open()
    },
    [open]
  )
}
